import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useMasters, nameOf } from "@/lib/masters";
import { PageHeader, Card, Btn, Input, Select, Field } from "@/components/kit";
import { toast, Toaster } from "sonner";
import { Plus, X, PencilSimple } from "@phosphor-icons/react";
import { useAuth } from "@/lib/auth";
import { istDateString } from "@/lib/date";

export default function Purchases() {
  const m = useMasters();
  const { user } = useAuth();
  const canWrite = user?.role === "super_admin" || (Array.isArray(user?.permissions)
    ? user.permissions.includes("purchase:write") : ["admin", "manager", "inventory"].includes(user?.role));
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState("");
  const [list, setList] = useState([]);
  const [show, setShow] = useState(false);
  const today = istDateString();
  const emptyForm = () => ({
    purchase_date: today, supplier_id: "", machine_id: "", plate_size_id: "",
    location_id: "", quantity: 0, supplier_invoice_number: "", vehicle_ref: "", remarks: "",
  });
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(async () => setList((await api.get("/inventory/purchases")).data), []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setReason(""); setForm(emptyForm()); setShow(true);  };
  const openEdit = (entry) => {
    const values = emptyForm();
    Object.keys(values).forEach(key => { values[key] = entry[key] ?? values[key]; });
    setEditing(entry); setReason(""); setForm(values); setShow(true); 
  };
  const close = () => { if (!busy) setShow(false); };

  const submit = async () => {
    if (busy) return;
    if (!Number.isInteger(Number(form.quantity)) || Number(form.quantity) <= 0) { toast.error("Enter a positive whole-number quantity"); return; }
    if (editing && !reason.trim()) { toast.error("Enter a reason for this edit"); return; }
    setBusy(true);
    try {
      const body = { ...form, quantity: Number(form.quantity) };
      if (!body.supplier_id) delete body.supplier_id;
      if (editing) await api.put(`/inventory/purchases/${editing.id}`, { ...body, expected_revision: editing.revision ?? 0, reason: reason.trim() });
      else await api.post("/inventory/purchases", body);
      toast.success(editing ? "Purchase updated, stock corrected" : "Purchase saved, stock added");
      setShow(false); await load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="purchases-page">
      <Toaster position="bottom-right" />
      <PageHeader title="Purchases" subtitle="Purchase entries automatically add to the stock ledger."
        actions={canWrite && <Btn onClick={openNew} data-testid="purchase-new-btn"><Plus size={14} /> New Purchase</Btn>} />
      <div className="p-6">
        <Card><div className="overflow-x-auto">
          <table className="w-full min-w-[850px] data-table" data-testid="purchases-table">
            <thead><tr>
              <th className="px-3 py-2 text-left">Purchase No.</th>
              <th className="px-3 py-2 text-left">Date</th>
              <th className="px-3 py-2 text-left">Supplier</th>
              <th className="px-3 py-2 text-left">Workshop</th>
              <th className="px-3 py-2 text-left">Size</th>
              <th className="px-3 py-2 text-left">Location</th>
              <th className="px-3 py-2 text-right">Qty</th>
              <th className="px-3 py-2 text-left">Invoice</th><th className="px-3 py-2 text-left">Actions</th>
            </tr></thead>
            <tbody>
              {list.length === 0 && <tr><td colSpan={9} className="text-center py-8 text-zinc-500 text-sm">No purchases.</td></tr>}
              {list.map(p => (
                <tr key={p.id}>
                  <td className="px-3 py-2 font-mono font-semibold">{p.purchase_number}</td>
                  <td className="px-3 py-2 font-mono">{p.purchase_date}</td>
                  <td className="px-3 py-2">{nameOf(m.suppliers, p.supplier_id)}</td>
                  <td className="px-3 py-2 font-mono">{nameOf(m.machines, p.machine_id)}</td>
                  <td className="px-3 py-2 font-mono">{m.plate_sizes.find(x=>x.id===p.plate_size_id)?.canonical || "-"}</td>
                  <td className="px-3 py-2">{nameOf(m.locations, p.location_id)}</td>
                  <td className="px-3 py-2 text-right font-mono font-bold">{p.quantity}</td>
                  <td className="px-3 py-2 font-mono">{p.supplier_invoice_number||"-"}</td>
                  <td className="px-3 py-2">{canWrite && p.status !== "cancelled" && <Btn variant="outline" onClick={()=>openEdit(p)} data-testid={`purchase-edit-${p.id}`}><PencilSimple size={14}/> Edit</Btn>}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
        </Card>
      </div>

      {show && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={close}>
          <div
            aria-labelledby="purchase-form-title"
            aria-modal="true"
            className="bg-white rounded-sm border border-zinc-200 w-full max-w-2xl max-h-[90dvh] overflow-y-auto"
            data-testid="purchase-form-dialog"
            onClick={e => e.stopPropagation()}
            role="dialog"
          >
            <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-200">
              <div id="purchase-form-title" className="font-heading font-bold">
                {editing ? `Edit ${editing.purchase_number}` : "New Purchase"}
              </div>
              <button disabled={busy} onClick={close} aria-label="Close form"><X size={18} /></button>
            </div>
            <div className="p-5 grid grid-cols-2 gap-3">
              <Field label="Date*"><Input type="date" value={form.purchase_date} onChange={e=>setForm({...form,purchase_date:e.target.value})} /></Field>
              <Field label="Supplier">
                <Select value={form.supplier_id} onChange={e=>setForm({...form,supplier_id:e.target.value})}>
                  <option value="">—</option>
                  {m.suppliers.map(x=><option key={x.id} value={x.id}>{x.name}</option>)}
                </Select>
              </Field>
              <Field label="Machine*">
                <Select value={form.machine_id} onChange={e=>setForm({...form,machine_id:e.target.value})} data-testid="purchase-machine">
                  <option value="">—</option>
                  {m.machines.map(x=><option key={x.id} value={x.id}>{x.name}</option>)}
                </Select>
              </Field>
              <Field label="Plate Size*">
                <Select value={form.plate_size_id} onChange={e=>setForm({...form,plate_size_id:e.target.value})} data-testid="purchase-size">
                  <option value="">—</option>
                  {m.plate_sizes.map(x=><option key={x.id} value={x.id}>{x.canonical}</option>)}
                </Select>
              </Field>
              <Field label="Location*">
                <Select value={form.location_id} onChange={e=>setForm({...form,location_id:e.target.value})}>
                  <option value="">—</option>
                  {m.locations.map(x=><option key={x.id} value={x.id}>{x.name}</option>)}
                </Select>
              </Field>
              <Field label="Quantity*"><Input type="number" min="1" value={form.quantity} onChange={e=>setForm({...form,quantity:e.target.value})} data-testid="purchase-qty" /></Field>
              <Field label="Invoice No."><Input value={form.supplier_invoice_number} onChange={e=>setForm({...form,supplier_invoice_number:e.target.value})} /></Field>
              <Field label="Vehicle Ref."><Input value={form.vehicle_ref} onChange={e=>setForm({...form,vehicle_ref:e.target.value})} /></Field>
              <div className="col-span-2"><Field label="Remarks"><Input value={form.remarks} onChange={e=>setForm({...form,remarks:e.target.value})} /></Field></div>
            </div>
            {editing && <div className="px-5 pb-4"><Field label="Reason for edit*"><Input value={reason} maxLength={500} onChange={e=>setReason(e.target.value)} data-testid="inventory-edit-reason" /></Field><p className="text-xs text-zinc-500 mt-2">Saving corrects stock and records the change in the audit log.</p></div>}
            <div className="flex justify-end gap-2 px-5 py-3 border-t border-zinc-200">
              <Btn disabled={busy} variant="outline" onClick={close}>Close</Btn>
              <Btn disabled={busy} onClick={submit} data-testid="purchase-submit-btn">{busy ? "Saving…" : editing ? "Save Changes" : "Save"}</Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

